import React, { useMemo, useState } from "react";
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Line } from "react-chartjs-2";
import {
  Activity,
  CalendarDays,
  ChevronDown,
  Clock3,
  RefreshCw,
  TrendingUp,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react";

import "./GreeksAnalyser.css";
import { environment } from "environment/environment";
import usePageTitle from "component/common/PageTitle";
import useAngelOneGreeksWebsocket from "service/websocket/AngelOneGreeksWebsocket";

ChartJS.register(CategoryScale, Filler, Legend, LinearScale, LineElement, PointElement, Title, ChartTooltip);

const METRICS = [
  { key: "vega", label: "VEGA", eyebrow: "VOLATILITY FLOW", precision: 2, icon: Zap },
  { key: "iv", label: "IMPLIED VOLATILITY", eyebrow: "PREMIUM PRESSURE", precision: 2, icon: Activity },
  { key: "gamma", label: "GAMMA", eyebrow: "CONVEXITY FLOW", precision: 4, icon: TrendingUp },
];

const ANALYSER_TABS = [
  { key: "GREEKS_ANALYSER", label: "GREEKS ANALYSER" },
  { key: "GREEKS_GRAPH", label: "GREEKS GRAPH" },
];

const MAX_INTRADAY_POINTS = 390;

const asNumber = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") continue;
    const parsed = Number(String(value).replace(/,/g, ""));
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const metricSnapshot = (value) => ({
  call: asNumber(value?.call),
  put: asNumber(value?.put),
  difference: asNumber(value?.difference),
});

const emptyMetric = () => ({ call: null, put: null, difference: null });

const metricValuesForPoint = (point, metric) => {
  if (metric.key === "vega" || metric.key === "iv" || metric.key === "gamma") {
    const storedChange = point?.changeMetrics?.[metric.key];
    if (storedChange && (storedChange.call !== null || storedChange.put !== null)) {
      return storedChange;
    }
  }
  return point?.metrics?.[metric.key] || emptyMetric();
};

const snapshotFromAngelOne = (source) => {
  if (!source) return null;

  const metrics = {
    vega: metricSnapshot(source.currentVega),
    iv: metricSnapshot(source.currentIv),
    gamma: metricSnapshot(source.currentGamma),
  };
  const openingMetrics = {
    vega: metricSnapshot(source.dayOpenVega),
    iv: metricSnapshot(source.dayOpenIv),
    gamma: metricSnapshot(source.dayOpenGamma),
  };
  const changeMetrics = {
    vega: metricSnapshot(source.changeVega),
    iv: metricSnapshot(source.changeIv),
    gamma: metricSnapshot(source.changeGamma),
  };
  const hasMetric = METRICS.some((metric) => (
    metrics[metric.key].call !== null || metrics[metric.key].put !== null
  ));
  if (!hasMetric) return null;

  return {
    id: `${source.accountName || "angelone"}-${source.createdDate || "live"}-${source.createdTime || "now"}`,
    time: source.createdTime || "--",
    date: source.createdDate || "",
    expiry: source.expiryDate || "--",
    spot: asNumber(source.indexSpotPrice),
    metrics,
    openingMetrics,
    changeMetrics,
  };
};

const formatValue = (value, precision = 2) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "--";
  return Number(value).toFixed(precision);
};

const signedValue = (value, precision = 2) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return "--";
  const number = Number(value);
  return `${number > 0 ? "+" : ""}${number.toFixed(precision)}`;
};

const diff = (metric) => {
  if (metric?.call === null || metric?.put === null) return null;
  return metric.call - metric.put;
};

const graphDiff = (metric) => metric?.difference ?? diff(metric);

const change = (current, opening) => ({
  call: current?.call !== null && opening?.call !== null ? current.call - opening.call : null,
  put: current?.put !== null && opening?.put !== null ? current.put - opening.put : null,
});

const trendFor = (value) => (value > 0 ? "BEARISH" : value < 0 ? "BULLISH" : "NEUTRAL");

const trendToneFor = (value) => (value > 0 ? "negative" : value < 0 ? "positive" : "neutral");

const toneFor = (value) => (value > 0 ? "positive" : value < 0 ? "negative" : "neutral");

const formatHeaderDate = (value) => {
  if (!value) return "--";
  const normalized = String(value).slice(0, 10);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : String(value);
};

const formatHeaderTime = (value) => {
  if (!value) return "--";
  if (typeof value === "number") {
    return new Date(value).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
      timeZone: "Asia/Kolkata",
    });
  }
  const match = String(value).match(/(\d{2}:\d{2})(?::(\d{2}))?/);
  return match ? `${match[1]}${match[2] ? `:${match[2]}` : ""}` : String(value);
};

function MetricSummaryCard({ metric, summary }) {
  const Icon = metric.icon;
  const rows = [
    { label: "DAY OPEN", values: summary.opening, tone: "" },
    { label: "CURRENT", values: summary.current, tone: "" },
    { label: "DIFFERENCE", values: summary.change, tone: "difference" },
  ];

  return (
    <article className={`greeks-metric-card metric-${metric.key}`}>
      <div className="metric-card-heading">
        <div className="metric-heading-icon"><Icon size={18} strokeWidth={2.3} /></div>
        <div>
          <h2>{metric.label} <span className="metric-heading-flow">{metric.eyebrow}</span></h2>
        </div>
        <span className={`metric-trend ${trendToneFor(summary.netChange)}`}>{trendFor(summary.netChange)}</span>
      </div>

      <div className="metric-table" role="table" aria-label={`${metric.label} summary`}>
        <div className="metric-table-row metric-table-head" role="row">
          <span>SNAPSHOT</span><span>CALL {graphMetricLabel(metric)}</span><span>PUT {graphMetricLabel(metric)}</span><span>DIFF</span>
        </div>
        {rows.map((row) => (
          <div className={`metric-table-row ${row.tone}`} role="row" key={row.label}>
            <strong>{row.label}</strong>
            <span className={row.tone === "difference" ? toneFor(row.values.call) : ""}>{row.tone === "difference" ? signedValue(row.values.call, metric.precision) : formatValue(row.values.call, metric.precision)}</span>
            <span className={row.tone === "difference" ? toneFor(row.values.put) : ""}>{row.tone === "difference" ? signedValue(row.values.put, metric.precision) : formatValue(row.values.put, metric.precision)}</span>
            <span className={row.tone === "difference" ? toneFor(row.values.diff) : ""}>{row.tone === "difference" ? signedValue(row.values.diff, metric.precision) : signedValue(row.values.diff, metric.precision)}</span>
          </div>
        ))}
      </div>
    </article>
  );
}

function HistoryCard({ metric, history }) {
  const Icon = metric.icon;

  return (
    <section className={`greeks-history-card metric-${metric.key}`}>
      <div className="history-card-heading">
        <div className="history-card-title">
          <div className="metric-heading-icon"><Icon size={18} strokeWidth={2.3} /></div>
          <h2>{metric.label} <span className="history-heading-flow">INTRADAY TRACKER</span></h2>
        </div>
        <span className="history-count">{history.length} points</span>
      </div>
      <div className="history-table-wrap">
        <table className="history-table">
          <thead>
            <tr><th>TIME</th><th>CALL {graphMetricLabel(metric)}</th><th>PUT {graphMetricLabel(metric)}</th><th>DIFF</th><th>TREND</th></tr>
          </thead>
          <tbody>
            {[...history].reverse().map((point) => {
              const values = metricValuesForPoint(point, metric);
              const difference = diff(values);
              return (
                <tr key={`${metric.key}-${point.id || point.time}`}>
                  <td className="history-time">{point.time}</td>
                  <td>{formatValue(values?.call, metric.precision)}</td>
                  <td>{formatValue(values?.put, metric.precision)}</td>
                  <td className={toneFor(difference)}>{signedValue(difference, metric.precision)}</td>
                  <td><span className={`table-trend ${trendToneFor(difference)}`}>{trendFor(difference)}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const GRAPH_SERIES = [
  { key: "call", label: "CALL", color: "#008a0b", fill: "rgba(0, 138, 11, .08)" },
  { key: "put", label: "PUT", color: "#ef1717", fill: "rgba(239, 23, 23, .06)" },
  { key: "difference", label: "DIFFERENCE", color: "#253247", fill: "rgba(37, 50, 71, .04)" },
];

const graphMetricLabel = (metric) => (metric.key === "iv" ? "IV" : metric.label);

function GreeksGraphView({ history }) {
  const [selectedMetric, setSelectedMetric] = useState("vega");
  const [visibleSeries, setVisibleSeries] = useState({ call: true, put: true, difference: false });
  const [chartRange, setChartRange] = useState("ALL");
  const metric = METRICS.find((item) => item.key === selectedMetric) || METRICS[0];
  const metricLabel = graphMetricLabel(metric);
  const chartHistory = [...history].sort((left, right) => String(left.time).localeCompare(String(right.time)));
  const chartRangeSize = chartRange === "ALL" ? chartHistory.length : Number(chartRange);
  const visibleChartHistory = chartRangeSize >= chartHistory.length
    ? chartHistory
    : chartHistory.slice(-chartRangeSize);
  const current = metricValuesForPoint(history[history.length - 1], metric);
  const currentDifference = graphDiff(current);

  const chartData = {
    labels: visibleChartHistory.map((point) => point.time),
    datasets: GRAPH_SERIES.map((series) => ({
      label: series.key === "difference" ? series.label : `${series.label} ${metricLabel}`,
      data: visibleChartHistory.map((point) => {
        const values = metricValuesForPoint(point, metric);
        return series.key === "difference" ? graphDiff(values) : values[series.key];
      }),
      borderColor: series.color,
      backgroundColor: series.fill,
      borderWidth: 4,
      pointRadius: visibleChartHistory.length > 35 ? 0 : 2.5,
      pointHoverRadius: 5,
      pointBorderWidth: 2,
      tension: 0.28,
      fill: false,
      hidden: !visibleSeries[series.key],
      spanGaps: true,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: false,
      },
      title: { display: false },
      tooltip: {
        titleFont: { size: 17, weight: "700" },
        bodyFont: { size: 17, weight: "600" },
        footerFont: { size: 17, weight: "600" },
        padding: 12,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${formatValue(context.parsed.y, metric.precision)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: "rgba(148, 163, 184, .22)" },
        ticks: { color: "#64748b", maxRotation: 0, autoSkip: true, maxTicksLimit: 12 },
      },
      y: {
        grid: {
          color: (context) => (context.tick.value === 0 ? "#111827" : "rgba(148, 163, 184, .22)"),
          lineWidth: (context) => (context.tick.value === 0 ? 4 : 1),
        },
        ticks: {
          color: "#64748b",
          callback: (value) => formatValue(value, metric.precision),
        },
      },
    },
  };

  const toggleSeries = (seriesKey) => {
    setVisibleSeries((currentVisibility) => ({
      ...currentVisibility,
      [seriesKey]: !currentVisibility[seriesKey],
    }));
  };

  return (
    <section className="greeks-graph-view" aria-label="Greeks graph view">
      <div className="greeks-graph-metric-tabs" role="tablist" aria-label="Greek metric">
        {METRICS.map((item) => (
          <button
            type="button"
            role="tab"
            aria-selected={selectedMetric === item.key}
            className={selectedMetric === item.key ? "active" : ""}
            onClick={() => setSelectedMetric(item.key)}
            key={item.key}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="greeks-graph-summary-grid">
        <article className="greeks-graph-summary-card graph-call-card">
          <strong>CALL {metricLabel}</strong>
          <span>{formatValue(current.call, metric.precision)}</span>
        </article>
        <article className="greeks-graph-summary-card graph-put-card">
          <strong>PUT {metricLabel}</strong>
          <span>{formatValue(current.put, metric.precision)}</span>
        </article>
        <article className="greeks-graph-summary-card graph-difference-card">
          <strong>DIFFERENCE</strong>
          <span className={toneFor(currentDifference)}>{signedValue(currentDifference, metric.precision)}</span>
        </article>
      </div>

      <div className="greeks-graph-layout">
        <article className="greeks-chart-panel">
          <div className="greeks-chart-toolbar">
            <div>
              <strong>{metric.label} FLOW</strong>
            </div>
            <div className="greeks-chart-controls">
              <div className="greeks-chart-range" aria-label="Chart range">
                <span>VIEW</span>
                {[{ value: "ALL", label: "ALL" }, { value: "120", label: "120" }, { value: "60", label: "60" }, { value: "30", label: "30" }].map((range) => (
                  <button
                    type="button"
                    className={chartRange === range.value ? "active" : ""}
                    aria-pressed={chartRange === range.value}
                    onClick={() => setChartRange(range.value)}
                    key={range.value}
                  >
                    {range.label}
                  </button>
                ))}
              </div>
              <div className="greeks-series-toggle" aria-label="Chart series">
                {GRAPH_SERIES.map((series) => (
                  <button
                    type="button"
                    className={visibleSeries[series.key] ? "active" : ""}
                    aria-pressed={visibleSeries[series.key]}
                    onClick={() => toggleSeries(series.key)}
                    key={series.key}
                  >
                    <span style={{ backgroundColor: series.color }} />{series.key === "difference" ? series.label : `${series.label} ${metricLabel}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="greeks-chart-wrap">
            <Line data={chartData} options={chartOptions} />
          </div>
        </article>

        <article className="greeks-graph-history-panel">
          <div className="greeks-graph-history-heading">
            <div>
              <strong>{metric.label} HISTORY</strong>
            </div>
            <span>{history.length}</span>
          </div>
          <div className="greeks-graph-table-wrap">
            <table className="greeks-graph-table">
              <thead>
                <tr><th>TIME</th><th>CALL {metricLabel}</th><th>PUT {metricLabel}</th><th>DIFFERENCE</th><th>TREND</th></tr>
              </thead>
              <tbody>
                {[...history].reverse().map((point) => {
                  const values = metricValuesForPoint(point, metric);
                  const difference = graphDiff(values);
                  return (
                    <tr key={`${selectedMetric}-${point.id || point.time}`}>
                      <td>{point.time}</td>
                      <td>{formatValue(values.call, metric.precision)}</td>
                      <td>{formatValue(values.put, metric.precision)}</td>
                      <td className={toneFor(difference)}>{signedValue(difference, metric.precision)}</td>
                      <td><span className={`graph-table-trend ${trendToneFor(difference)}`}>{trendFor(difference)}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </article>
      </div>
    </section>
  );
}

export default function GreeksAnalyser() {
  const { history: angelOneHistory, isConnected, lastUpdated } = useAngelOneGreeksWebsocket(
    environment.angelOneGreeksWebsocketUrl,
    environment.angelOneGreeksTopic,
  );
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [expiry, setExpiry] = useState("--");
  const [activeTab, setActiveTab] = useState("GREEKS_ANALYSER");
  const [isRefreshing, setIsRefreshing] = useState(false);

  usePageTitle();

  const liveHistory = useMemo(
    () => angelOneHistory.map(snapshotFromAngelOne).filter(Boolean).slice(-MAX_INTRADAY_POINTS),
    [angelOneHistory],
  );

  const history = liveHistory;
  const openingValues = history[history.length - 1]?.openingMetrics || history[0]?.metrics;
  const currentValues = history[history.length - 1]?.metrics || {};
  const currentSpot = history[history.length - 1]?.spot;
  const openingSpot = history[0]?.spot;
  const currentExpiry = history[history.length - 1]?.expiry || expiry;
  const headerDate = formatHeaderDate(history[history.length - 1]?.date || selectedDate);
  const headerTime = formatHeaderTime(lastUpdated || history[history.length - 1]?.time);

  const summaries = useMemo(() => Object.fromEntries(METRICS.map((metric) => {
    const opening = openingValues?.[metric.key] || { call: null, put: null };
    const current = currentValues?.[metric.key] || { call: null, put: null };
    const currentDifference = diff(current);
    const openingDifference = diff(opening);
    const storedChanges = history[history.length - 1]?.changeMetrics?.[metric.key];
    const changes = storedChanges && (storedChanges.call !== null || storedChanges.put !== null)
      ? storedChanges
      : change(current, opening);
    const changeDifference = diff(changes);
    return [metric.key, {
      opening: { ...opening, diff: openingDifference },
      current: { ...current, diff: currentDifference },
      change: { ...changes, diff: changeDifference },
      netChange: changeDifference,
    }];
  })), [currentValues, history, openingValues]);

  const spotChange = currentSpot !== null && currentSpot !== undefined && openingSpot !== null && openingSpot !== undefined
    ? currentSpot - openingSpot
    : null;

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.setTimeout(() => setIsRefreshing(false), 650);
  };

  return (
    <main className="greeks-analyser-page">
      <header className="greeks-stock-header">
        <nav className="greeks-stock-nav">
          <div className="greeks-stock-left-nav">
            <div className="greeks-stock-logo-container">
              <img
                src="/assets/images/noptionwave-logo-1.png"
                className="greeks-stock-header-logo"
                alt="noptionwave logo"
              />
            </div>
          </div>
          <div className="greeks-stock-right-nav">
            <div className="greeks-stock-header-item">
              <span className="greeks-stock-header-line">Date : {headerDate}</span>
              <span className="greeks-stock-header-line">Time : {headerTime}</span>
            </div>
          </div>
        </nav>
      </header>

      <div className="greeks-tab-container">
        <div className="greeks-tab" role="tablist" aria-label="Greeks analyser views">
          {ANALYSER_TABS.map((tab) => (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`greeks-tablinks ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
              key={tab.key}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="greeks-analyser-shell">
        <section className="greeks-toolbar" aria-label="Greeks filters">
          <label className="greeks-control">
            <span>INDEX</span>
            <span className="greeks-select-wrap"><select defaultValue="NIFTY" aria-label="Index"><option>NIFTY</option></select><ChevronDown size={15} /></span>
          </label>
          <label className="greeks-control greeks-date-control">
            <span>SESSION DATE</span>
            <span className="greeks-date-wrap"><CalendarDays size={15} /><input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} aria-label="Session date" /></span>
          </label>
          <label className="greeks-control">
            <span>EXPIRY</span>
            <span className="greeks-select-wrap"><select value={currentExpiry} onChange={(event) => setExpiry(event.target.value)} aria-label="Expiry" disabled={!history.length}><option>{currentExpiry}</option></select><ChevronDown size={15} /></span>
          </label>
          <div className="greeks-toolbar-market" aria-label="NIFTY market summary">
            <div className="greeks-toolbar-market-main">
              <span className="market-label"><span className="market-dot" />NIFTY</span>
              <strong>{formatValue(currentSpot, 2)}</strong>
              <span className={`spot-change ${toneFor(spotChange)}`}>
                <strong>{signedValue(spotChange, 2)}</strong>
              </span>
            </div>
            <div className="greeks-toolbar-market-meta">
              <span className="market-meta-item"><span>Open</span><strong>{formatValue(openingSpot, 2)}</strong></span>
              <span className="market-meta-item"><span>Expiry</span><strong>{currentExpiry}</strong></span>
              <span className="market-meta-item"><span>Last update</span><strong>{lastUpdated ? new Date(lastUpdated).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }) : "--"}</strong></span>
            </div>
          </div>
          <div className="greeks-toolbar-note">
            <div className={`stream-status ${isConnected ? "connected" : "disconnected"}`}>
              {isConnected ? <Wifi size={15} /> : <WifiOff size={15} />}
              <span>{isConnected ? "Stream connected" : "Waiting for today’s 09:15 data"}</span>
            </div>
            <button type="button" className="greeks-refresh-button" onClick={handleRefresh} aria-label="Refresh Greeks data">
              <RefreshCw size={16} className={isRefreshing ? "refresh-spin" : ""} />
              <span>Refresh</span>
            </button>
            <span className="greeks-update-note"><Clock3 size={15} /><span>Updates every minute</span></span>
          </div>
        </section>

        {!history.length ? (
          <section className="greeks-empty-state" aria-live="polite">
            <Clock3 size={28} />
            <strong>No Greeks data available for today</strong>
            <span>Today’s day-open data will appear here after the 09:15 market-data load.</span>
          </section>
        ) : activeTab === "GREEKS_ANALYSER" ? (
          <>
        <section className="greeks-summary-grid">
          {METRICS.map((metric) => <MetricSummaryCard key={metric.key} metric={metric} summary={summaries[metric.key]} />)}
        </section>

        <section className="greeks-history-grid">
          {METRICS.map((metric) => <HistoryCard key={metric.key} metric={metric} history={history} />)}
        </section>

          </>
        ) : (
          <GreeksGraphView history={history} />
        )}
      </div>
      <footer className="greeks-page-footer">noptionwave © 2026</footer>
    </main>
  );
}
